import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChannelList, useChatContext } from 'stream-chat-react'
import { debounce } from 'lodash'
import SearchIcon from 'components/Common/SvgIcons/SearchIcon'
import { MessagingChannelList, MessagingChannelPreview } from 'components/Chat/components'
import { useUnreadContext } from 'components/Chat/contexts/UnreadContext'
import { Channel } from 'stream-chat'
import { DefaultStreamChatGenerics } from 'components/Chat/types'
import { useTenantId } from 'utils/useTenantId'
import { ampli } from 'ampli'
import UserGroupIcon from 'assets/svgs/user-group.svg'
import PencilSquareIcon from 'assets/svgs/pencil-square.svg'
import { getChannelName, getInitials } from '../../helper'
import { getSearchOperator } from '../../utils/searchHelpers'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'

// Stream counts system messages as unread, but the timeline hides them, so leave them out
const countVisibleUnread = (channel: Channel<DefaultStreamChatGenerics>) => {
  const lastRead = channel.lastRead()
  const hiddenUnread = channel.state.latestMessages.filter(
    (message) =>
      message.type === 'system' &&
      (!lastRead || message.created_at > lastRead) &&
      channel._countMessageAsUnread(message)
  ).length
  return Math.max(channel.countUnread() - hiddenUnread, 0)
}

const ChannelListHeader = ({
  userId,
  usersAssessmentResponseByQuestion,
  setShowChannelCreate,
  showChannelCreate,
  setShowChannelPreview,
  showChannelPreview,
  chatUnreadMessageCountAction,
  chatUnreadMessageCount,
  setShowUserSelection,
  showUserSelection,
  setShowGroupCreation,
  showGroupCreation,
  isSelectingChannel = false,
  setInitialJumpMessageId,
}) => {
  const { t } = useTranslation()
  const tenantId = useTenantId()
  const { channel: activeChannel, setActiveChannel, client } = useChatContext()

  const { setChatsUnreadCount, channelMessageCount, setChannelMessageCount } = useUnreadContext()
  const [channelFilters] = useState<any>({
    type: 'messaging',
    $and: [{ team: { $in: [tenantId] } }, { members: { $in: [userId] } }],
  })

  const [searchChannel, setSearchChannel] = useState('')
  const [inputValue, setInputValue] = useState('') // Immediate value for input display
  const [channelMatchingMessages, setChannelMatchingMessages] = useState<Map<string, any>>(
    new Map()
  )
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearchResults, setHasSearchResults] = useState(true)
  const didInitialAutoSelectRef = useRef(false)
  const stableSearchResultsRef = useRef<Map<string, any>>(new Map())
  const latestSearchRef = useRef('')

  // Function to search messages across all channels
  const searchMessages = useCallback(
    async (query: string) => {
      if (!query.trim() || !client) {
        setChannelMatchingMessages(new Map())
        stableSearchResultsRef.current = new Map()
        return
      }

      latestSearchRef.current = query
      setIsSearching(true)
      try {
        // Server-side search over every conversation of this tenant the user is a member of
        // (not only the loaded channels / their latest messages), newest first
        const response = await client.search(channelFilters, getSearchOperator(query), {
          sort: [{ created_at: -1 }],
          limit: 100,
        })
        if (latestSearchRef.current !== query) return

        const channelMessageMap = new Map()
        for (const { message } of response.results || []) {
          // Same messages as the timeline: skip system messages and thread-only replies
          if (!message.cid || message.type === 'system') continue
          if (message.parent_id && !message.show_in_channel) continue
          channelMessageMap.set(message.cid, [
            ...(channelMessageMap.get(message.cid) || []),
            message,
          ])
        }

        // Load matching conversations that are not in the channel list yet so they can be opened
        const missingCids = Array.from(channelMessageMap.keys()).filter(
          (cid) => !client.activeChannels[cid]
        )
        for (let i = 0; i < missingCids.length; i += 30) {
          await client.queryChannels(
            { ...channelFilters, cid: { $in: missingCids.slice(i, i + 30) } },
            {},
            { state: true, watch: true, presence: true, limit: 30 }
          )
        }
        if (latestSearchRef.current !== query) return

        setChannelMatchingMessages(channelMessageMap)
        stableSearchResultsRef.current = channelMessageMap
      } catch (error) {
        console.error('Error searching messages:', error)
        if (latestSearchRef.current !== query) return
        setChannelMatchingMessages(new Map())
        stableSearchResultsRef.current = new Map()
      } finally {
        if (latestSearchRef.current === query) setIsSearching(false)
      }
    },
    [client, channelFilters]
  )

  // Debounced function for both state update and search
  const debouncedHandleSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchChannel(value)
        if (value.trim()) {
          searchMessages(value)
        } else {
          // Drop any search still in flight so it cannot bring back results or the spinner
          latestSearchRef.current = ''
          setIsSearching(false)
          setChannelMatchingMessages(new Map())
          stableSearchResultsRef.current = new Map()
        }
      }, 300),
    [searchMessages]
  )

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedHandleSearch.cancel()
    }
  }, [debouncedHandleSearch])

  const customRenderFilter = (channels) => {
    try {
      // The open channel is being marked read, so its count may still be the stale pre-read value
      const unreadOf = (channel) =>
        channel.cid === activeChannel?.cid ? 0 : countVisibleUnread(channel)
      setChatsUnreadCount(channels.reduce((total, channel) => total + unreadOf(channel), 0))

      channels.forEach((channel) => (channelMessageCount[channel.cid] = unreadOf(channel)))
      setChannelMessageCount(channelMessageCount)

      const searchActive = Boolean(searchChannel.trim())

      const filteredChannels = searchActive
        ? channels
            .filter((channel) => {
              // Use channelMatchingMessages if available, otherwise fall back to stable ref
              const searchResults =
                channelMatchingMessages.size > 0
                  ? channelMatchingMessages
                  : stableSearchResultsRef.current

              if (searchResults.size > 0) {
                return searchResults.has(channel.cid)
              }

              // Fallback to user name matching if no message search results
              const members = Object.values(channel?.state?.members || {})
              const query = searchChannel.toLowerCase()

              return members.some(({ user }: any) => {
                if (!user) return false
                const fullName = `${user.firstName || ''}${user.lastName || ''}`.toLowerCase()
                const displayName = (user.name || '').toLowerCase()
                const businessName = (user.businessName || '').toLowerCase()

                return (
                  (fullName && fullName.includes(query)) ||
                  (displayName && displayName.includes(query)) ||
                  (businessName && businessName.includes(query))
                )
              })
            })
            .sort((a, b) => {
              // Sort by latest matching message timestamp
              const searchResults =
                channelMatchingMessages.size > 0
                  ? channelMatchingMessages
                  : stableSearchResultsRef.current

              if (searchResults.size > 0) {
                const messagesA = searchResults.get(a.cid) || []
                const messagesB = searchResults.get(b.cid) || []

                if (messagesA.length > 0 && messagesB.length > 0) {
                  // Get the latest message from each channel (already sorted by newest first)
                  const latestA = new Date(messagesA[0].created_at || 0).getTime()
                  const latestB = new Date(messagesB[0].created_at || 0).getTime()
                  return latestB - latestA // Descending order (newest first)
                }
                if (messagesA.length > 0) return -1
                if (messagesB.length > 0) return 1
              }

              // Fallback: sort by channel's last message
              const lastMessageA = a.state.messages?.[a.state.messages.length - 1]
              const lastMessageB = b.state.messages?.[b.state.messages.length - 1]
              const timeA = lastMessageA ? new Date(lastMessageA.created_at || 0).getTime() : 0
              const timeB = lastMessageB ? new Date(lastMessageB.created_at || 0).getTime() : 0
              return timeB - timeA
            })
        : channels.slice()

      const nextHasResults = searchActive ? filteredChannels.length > 0 : true
      setHasSearchResults((prev) => (prev === nextHasResults ? prev : nextHasResults))

      // One-time initial auto-select on page load if no active channel and not in special modes
      if (
        !didInitialAutoSelectRef.current &&
        filteredChannels.length > 0 &&
        !activeChannel &&
        !showChannelCreate &&
        !showUserSelection &&
        !showGroupCreation &&
        !isSelectingChannel
      ) {
        didInitialAutoSelectRef.current = true
        setTimeout(() => {
          if (
            !activeChannel &&
            !showChannelCreate &&
            !showUserSelection &&
            !showGroupCreation &&
            !isSelectingChannel
          ) {
            setActiveChannel(filteredChannels[0])
            setShowChannelPreview(true)
          }
        }, 50)
      }

      // Ensure chat preview is shown when we have channels (unless creating new channel)
      if (filteredChannels.length > 0 && !showChannelCreate && !showChannelPreview) {
        setShowChannelPreview(true)
      }

      return filteredChannels
    } catch (err) {
      return channels
    }
  }

  const searchQueryHandler = (e) => {
    // Search is now handled by the useEffect with debouncing
    // This function can be removed or used for additional Enter key handling if needed
  }

  const handleNewMessage = () => {
    setShowUserSelection?.(true)
    setShowChannelCreate?.(false)
    setShowGroupCreation?.(false)
    ampli.newMessageInitiated()
  }

  const handleCreateGroup = () => {
    setShowGroupCreation?.(true)
    setShowChannelCreate?.(false)
    setShowUserSelection?.(false)
  }

  // Show user selection or group creation UI
  if (showUserSelection) {
    return null // Let parent handle these views
  }

  return (
    <div
      className="h-full w-full bg-white flex flex-col"
      style={{ boxShadow: 'inset -1px 0 0 rgb(229, 231, 235)' }}
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-app-grey-25 h-[70px]">
        <h1 className="text-xl font-semibold text-app-black-100 mb-0">
          {t('chat.channelList.title')}
        </h1>
      </div>

      {/* Search Bar */}
      <div className="px-6 py-3 border-b border-app-grey-25">
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-app-grey-55" />
          <input
            type="text"
            placeholder={t('chat.channelList.searchPlaceholder')}
            value={inputValue}
            onChange={(e) => {
              const value = e.target.value
              setInputValue(value) // Update input immediately for responsive UI
              debouncedHandleSearch(value) // Debounce state update and search
            }}
            onKeyPress={searchQueryHandler}
            className="w-full pl-12 pr-4 py-2.5 bg-app-grey-20 border border-app-grey-25 rounded-full text-sm placeholder-app-grey-55 focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent"
          />
          {isSearching && (
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-app-primary-60"></div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="py-3 border-b border-app-grey-25">
        <button
          onClick={handleNewMessage}
          className="w-full flex items-center justify-between px-4 py-2.5 text-left text-app-black-100 hover:bg-app-grey-10 rounded-lg transition-colors duration-150"
        >
          <span className="font-medium text-sm">{t('chat.channelList.newMessage')}</span>
          <div className="w-6 h-6 flex items-center justify-center">
            <img src={PencilSquareIcon} alt="new-message" className="w-5 h-5" />
          </div>
        </button>

        <div className="my-1.5 h-px bg-app-grey-25" />

        <button
          onClick={handleCreateGroup}
          className="w-full flex items-center justify-between px-4 py-2.5 text-left text-app-black-100 hover:bg-app-grey-10 rounded-lg transition-colors duration-150"
        >
          <span className="font-medium text-sm">{t('chat.channelList.createGroup')}</span>
          <div className="w-6 h-6 flex items-center justify-center">
            <img src={UserGroupIcon} alt="create-group" className="w-5 h-5" />
          </div>
        </button>
      </div>

      {/* Search Results View - Show all matching messages when searching */}
      {searchChannel.trim() && channelMatchingMessages.size > 0 ? (
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden w-full pb-6">
          <div className="px-4 py-4 w-full">
            {Array.from(channelMatchingMessages.entries()).map(([cid, messages]) => {
              const channel = Object.values(client.activeChannels || {}).find(
                (c: any) => c?.cid === cid
              ) as any

              // Get members and use the getChannelName helper
              const members = Object.values(channel?.state?.members || {}).filter(
                (m: any) => m.user?.id !== userId
              )
              const channelName =
                channel?.data?.name || getChannelName(members as any) || 'Unknown Channel'
              return messages.map((msg: any, idx: number) => {
                // Decode HTML entities and strip tags
                const decodeHtml = (html: string) => {
                  const txt = document.createElement('textarea')
                  txt.innerHTML = html
                  return txt.value.replace(/<[^>]*>/g, '')
                }
                const messageText = msg.text ? decodeHtml(msg.text) : '(attachment)'
                const userInitials = getInitials({
                  firstName: msg.user?.firstName,
                  lastName: msg.user?.lastName,
                })

                return (
                  <div
                    key={`${msg.id}-${idx}`}
                    className="px-2 py-2 hover:bg-app-grey-10 cursor-pointer transition-colors duration-150 rounded flex items-start gap-3"
                    onClick={() => {
                      if (channel) {
                        setActiveChannel(channel)
                        setInitialJumpMessageId?.(msg.id)
                        setShowChannelPreview?.(true)
                      }
                    }}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <AppAvatar
                        size="md"
                        type={msg.user?.image ? 'image' : 'mixed'}
                        shape="Rounded"
                        text={userInitials}
                        src={msg.user?.image}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-app-grey-100 mb-1">
                        {channelName}
                      </div>
                      <p className="text-sm text-app-grey-80 line-clamp-1">{messageText}</p>
                    </div>
                  </div>
                )
              })
            })}
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden w-full pb-6">
          <ChannelList
            channelRenderFilterFn={customRenderFilter}
            filters={channelFilters}
            options={{ state: true, watch: true, presence: true, limit: 30 }}
            List={(props) => (
              <MessagingChannelList
                {...props}
                isSearching={isSearching}
                hasSearchResults={hasSearchResults}
                searchQuery={searchChannel}
              />
            )}
            setActiveChannelOnMount={false}
            Preview={(props) => (
              <MessagingChannelPreview
                {...(props as any)}
                searchChannel={searchChannel}
                showChannelCreate={showChannelCreate}
                setShowChannelCreate={setShowChannelCreate}
                setShowChannelPreview={setShowChannelPreview}
                usersAssessmentResponseByQuestion={usersAssessmentResponseByQuestion}
                chatUnreadMessageCountAction={chatUnreadMessageCountAction}
                channelMatchingMessages={channelMatchingMessages}
                isSearching={isSearching}
                setInitialJumpMessageId={setInitialJumpMessageId}
              />
            )}
          />
        </div>
      )}
    </div>
  )
}

export default ChannelListHeader
